import type { ClassificationResult } from '@classification-testing/domain';
import { ClassificationType, ContentType } from '@classification-testing/domain';
import { Card, Text } from '@soteria/design-system-ve';
import { useState } from 'react';

import { ClassificationCardControls } from './ClassificationCardControls';
import { ClassificationResultDialog } from './ClassificationResultDialog';
import * as styles from './ClassificationResultItem.css';
import { MetadataSection } from './MetadataSection';

interface ClassificationResultItemProps {
  result: ClassificationResult;
  _classifiedText: string;
  _promptVersion: string;
}

// Helper function to determine safety status
function getSafetyStatus(result: ClassificationResult) {
  const isSafe = result.classificationType === ClassificationType.SAFE_CONTENT;
  const primaryConcern = result.labels?.length > 0 ? result.labels.reduce((prev, current) => (prev.severity > current.severity ? prev : current)) : null;

  return { isSafe, primaryConcern };
}

// Helper function to get content preview
function getContentInfo(result: ClassificationResult, isSafe: boolean) {
  const content = result.text;
  const preview = content.length > 150 ? `${content.substring(0, 150)}...` : content || `${result.contentType} content`;
  const shouldBlurText = !isSafe && result.text;

  return { preview, shouldBlurText };
}

// Content preview component
function ContentPreview({ preview, shouldBlurText, isContentBlurred }: { preview: string; shouldBlurText: string | false; isContentBlurred: boolean }) {
  if (!preview) return null;

  const applyBlur = shouldBlurText && isContentBlurred;

  return (
    <div className={styles.contentPreviewSection}>
      <Text className={`${styles.previewText} ${applyBlur ? styles.blurredText : ''}`}>{preview}</Text>
    </div>
  );
}

function ImageThumbnail({ imageUrl, altText, isSafe, isContentBlurred }: { imageUrl: string; altText: string; isSafe: boolean; isContentBlurred: boolean }) {
  const [imageError, setImageError] = useState(false);

  if (imageError) {
    return (
      <div aria-label={altText} className={styles.thumbnailContainer} role="img">
        <Text className={styles.thumbnailErrorText}>Image unavailable</Text>
      </div>
    );
  }

  const applyBlur = !isSafe && isContentBlurred;

  return (
    <div className={styles.thumbnailContainer}>
      <img
        alt={altText}
        className={`${styles.thumbnailImage} ${applyBlur ? styles.blurredThumbnail : ''}`}
        src={imageUrl}
        onError={() => setImageError(true)}
      />
    </div>
  );
}

// Helper component to reduce main component complexity
function ClassificationCardContent({
  result,
  isSafe,
  preview,
  shouldBlurText,
  isContentBlurred,
  onToggleBlur,
}: {
  result: ClassificationResult;
  isSafe: boolean;
  preview: string;
  shouldBlurText: string | false;
  isContentBlurred: boolean;
  onToggleBlur: (e: React.MouseEvent) => void;
}) {
  const hasImageUrl = result.contentType === ContentType.IMAGE && result.contentUrl;

  return (
    <div
      className={`${styles.contentSummary} ${hasImageUrl ? styles.contentSummaryWithImage : ''} ${isSafe ? styles.safeContentBorder : styles.unsafeContentBorder}`}
    >
      {/* Image thumbnail column (left side) */}
      {hasImageUrl && (
        <ImageThumbnail
          altText={`Classified image from ${result.timestamp}`}
          imageUrl={result.contentUrl || ''}
          isContentBlurred={isContentBlurred}
          isSafe={isSafe}
        />
      )}

      {/* Content column (right side or full width if no image) */}
      <div className={hasImageUrl ? styles.contentColumn : ''}>
        <ContentPreview isContentBlurred={isContentBlurred} preview={preview} shouldBlurText={shouldBlurText} />
        <div className={styles.metadataRow}>
          <MetadataSection result={result} />
          <ClassificationCardControls isContentBlurred={isContentBlurred} isSafe={isSafe} result={result} onToggleBlur={onToggleBlur} />
        </div>
      </div>
    </div>
  );
}

export function ClassificationResultItem({ result }: ClassificationResultItemProps) {
  const [showDialog, setShowDialog] = useState(false);
  const { isSafe } = getSafetyStatus(result);
  const [isContentBlurred, setIsContentBlurred] = useState(!isSafe);
  const { preview, shouldBlurText } = getContentInfo(result, isSafe);

  const handleCardClick = () => {
    setShowDialog(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setShowDialog(true);
    }
  };

  const handleToggleBlur = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening the dialog
    setIsContentBlurred(!isContentBlurred);
  };

  return (
    <>
      <Card className={styles.classificationCard} tabIndex={0} onClick={handleCardClick} onKeyDown={handleKeyDown}>
        <ClassificationCardContent
          isContentBlurred={isContentBlurred}
          isSafe={isSafe}
          preview={preview}
          result={result}
          shouldBlurText={shouldBlurText}
          onToggleBlur={handleToggleBlur}
        />
      </Card>

      <ClassificationResultDialog
        isContentBlurred={isContentBlurred}
        isOpen={showDialog}
        isSafe={isSafe}
        result={result}
        onClose={() => setShowDialog(false)}
        onToggleBlur={() => setIsContentBlurred(!isContentBlurred)}
      />
    </>
  );
}
